/**
 * Block-merge + grouping helpers for the article-block override
 * pipeline. Used by article/wiki detail layouts (host-supplied) to
 * combine template-level blocks with per-article overrides and split
 * the result by placement zone.
 *
 * <p>Pure and dependency-free so they can be tested in isolation and
 * reused across blog and wiki detail layouts. Direct port of
 * ThemeMaker's {@code lib/platform/articleBlocks.ts}.
 */

import type { ArticleBlock, BlockInstance } from './types';

/**
 * Grouped per-zone result of {@link groupArticleBlocks}. The inline
 * map is keyed by the paragraph index the block follows, so the body
 * walker can do an O(1) lookup at each paragraph boundary.
 */
export interface GroupedBlocks {
  top: BlockInstance[];
  bottom: BlockInstance[];
  sidebar: BlockInstance[];
  inline: Map<number, BlockInstance[]>;
}

/**
 * Combine template-level blocks with per-article overrides. Order is
 * additive — template entries first within each placement, then the
 * per-article entries.
 *
 * <p>Authors who want their per-article block to appear BEFORE the
 * template block can't get that with this contract — they'd need to
 * remove the template block, which isn't currently expressible. If a
 * real "prepend" use case shows up, add an {@code order: 'before' |
 * 'after'} field on per-article entries.
 *
 * <p>Empty/undefined inputs are tolerated. Both undefined → empty array.
 */
export function mergeArticleBlocks(
  templateBlocks: ArticleBlock[] | undefined,
  perArticleBlocks: ArticleBlock[] | undefined,
): ArticleBlock[] {
  const t = templateBlocks ?? [];
  const a = perArticleBlocks ?? [];
  if (t.length === 0) return a;
  if (a.length === 0) return t;
  return [...t, ...a];
}

/**
 * Split blocks by placement into the four zones the detail layouts
 * render. Used after merging template + per-article blocks.
 *
 * <p>Robustness:
 * <ul>
 *   <li>undefined / null / empty input → all empty groups</li>
 *   <li>unknown placement strings → silently dropped (forward-compat
 *       for placement values added in future schema versions)</li>
 *   <li>{@code inline-after-paragraph-N}: only digit suffixes are
 *       accepted; bad numbers are dropped without erroring</li>
 * </ul>
 */
export function groupArticleBlocks(blocks: ArticleBlock[] | undefined): GroupedBlocks {
  const out: GroupedBlocks = {
    top: [],
    bottom: [],
    sidebar: [],
    inline: new Map(),
  };
  if (!blocks || blocks.length === 0) return out;

  for (const ab of blocks) {
    if (!ab || !ab.block) continue;
    const placement = ab.placement;

    if (placement === 'top') {
      out.top.push(ab.block);
    } else if (placement === 'bottom') {
      out.bottom.push(ab.block);
    } else if (placement === 'sidebar') {
      out.sidebar.push(ab.block);
    } else if (typeof placement === 'string' && placement.startsWith('inline-after-paragraph-')) {
      const numStr = placement.slice('inline-after-paragraph-'.length);
      const n = Number(numStr);
      if (Number.isInteger(n) && n > 0) {
        const existing = out.inline.get(n);
        if (existing) {
          existing.push(ab.block);
        } else {
          out.inline.set(n, [ab.block]);
        }
      }
    }
  }
  return out;
}
